import React, { useCallback } from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import { Controller } from "react-hook-form";
import TextInput from "../inputs/TextInput";
import { Button } from "../button";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import ComboboxInput from "../inputs/ComboboxInput";
import toast from "react-hot-toast";
import { pingOptions } from "../../constants/common";
import {
  useGetAllPersonsByIPFR,
  useGetFRTokenByIP,
  usePingDeviceByIP,
} from "../../hooks/internal-config/mutation";
import TextAreaInput from "../inputs/TextAreaInput";

export const GateBarrierConfigurationsCardSchema = Yup.object({
  ipAddress: Yup.string().required("IP Address is required"),
  token: Yup.string(),
  pingType: Yup.string(),
  response: Yup.string(),
});

interface FormValues {
  ipAddress: string;
  token?: string;
  pingType?: string;
  response?: string;
}

const GateBarrierConfigurationsCard = () => {
  const { t } = useTranslation();

  const { mutateAsync: PingDeviceByIP, isPending: isPingDeviceByIPLoading } =
    usePingDeviceByIP();

  const { mutateAsync: GetFRTokenByIP, isPending: isGetFRTokenByIPLoading } =
    useGetFRTokenByIP();

  const {
    mutateAsync: GetAllPersonsByIPFR,
    isPending: isGetAllPersonsByIPFRLoading,
  } = useGetAllPersonsByIPFR();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useFormWithSchema(GateBarrierConfigurationsCardSchema);

  const onPing = useCallback(
    async (data: FormValues) => {
      setValue("token", "");

      if (!data.pingType) {
        toast.error(t("Kindly select a ping type!"));
        return;
      }

      try {
        const body = {
          S_IPAddress: data.ipAddress,
          PingType: Number(data.pingType),
        };

        const { isDevicePing } = await PingDeviceByIP(body);

        if (isDevicePing) {
          toast.success(t("IP pinged successfully!"));
        } else {
          toast.error(t("IP ping failed!"));
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }
    },
    [PingDeviceByIP, t, setValue]
  );

  const onGetFRToken = useCallback(
    async (data: FormValues) => {
      setValue("token", "");

      try {
        const body = {
          S_IPAddress: data.ipAddress,
        };

        const res = await GetFRTokenByIP(body);

        const token = typeof res === "string" ? res : res?.result;

        setValue("token", token);
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }
    },
    [GetFRTokenByIP, setValue, t]
  );

  const onGetAllPersonByIP = useCallback(
    async (data: FormValues) => {
      setValue("token", "");

      try {
        const body = {
          ipAddress: data.ipAddress,
        };

        const res = await GetAllPersonsByIPFR(body);

        setValue("response", JSON.stringify(res, null, 2));
      } catch (error) {
        setValue("response", JSON.stringify(error, null, 2));
        toast.error(t("somethingWentWrong"));
      }
    },
    [GetAllPersonsByIPFR, setValue, t]
  );

  return (
    <form className="bg-white p-4 rounded-xl shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)] flex flex-col  gap-3 lg:gap-6">
      <h2 className="text-lg lg:text-xl font-medium">FR</h2>
      <div className="grid md:grid-cols-3 gap-3 lg:gap-6">
        <Controller
          name="ipAddress"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <TextInput
              label="IP Address"
              onChange={onChange}
              value={value || ""}
              error={errors?.[name]?.message}
              required
            />
          )}
        />

        <Controller
          name="pingType"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <ComboboxInput
              options={pingOptions}
              value={
                pingOptions?.find((item) => item.value === Number(value))
                  ?.labelEn || ""
              }
              onChange={onChange}
              error={errors?.[name]?.message}
              label={t("Ping Type")}
            />
          )}
        />

        <Controller
          name="token"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <TextInput
              label="Token"
              onChange={onChange}
              value={value || ""}
              error={errors?.[name]?.message}
              readOnly
            />
          )}
        />
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={handleSubmit(onPing)}
          isLoading={isPingDeviceByIPLoading}
        >
          {t("Ping Device")}
        </Button>
        <Button
          onClick={handleSubmit(onGetFRToken)}
          isLoading={isGetFRTokenByIPLoading}
        >
          {t("Get FR Token")}
        </Button>
        <Button
          onClick={handleSubmit(onGetAllPersonByIP)}
          isLoading={isGetAllPersonsByIPFRLoading}
        >
          {t("Get All Person By IP")}
        </Button>
      </div>

      <Controller
        name="response"
        control={control}
        render={({ field }) => (
          <TextAreaInput
            label="Response"
            {...field}
            value={field.value || ""}
            readOnly
            rows={15}
          />
        )}
      />
    </form>
  );
};

export default React.memo(GateBarrierConfigurationsCard);
